import { useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { AlertTriangle } from 'lucide-react';
import type { BankAccountDto } from '@signalvote/shared';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { PendingButton } from '@/components/ui/pending-button';
import {
  importBatchRefusal,
  useReattributeImport,
  type ImportBatchRefusal,
} from '@/hooks/queries/useFinanceImportBatches';
import { IMPORT_CHANGED_ELSEWHERE } from './importCopy';

const MOVE_FAILED = 'Something went wrong moving this import. Please try again.';

interface MoveImportDialogProps {
  batchId: string;
  originalFileName: string;
  /** The account the import sits on now; it is left out of the choices. */
  currentAccountId: string;
  /** The batch's token from the history row; the move sends it back. */
  concurrencyVersion: number;
  accounts: BankAccountDto[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Moves a bank-file import, and every bank line it brought in, to another of the
 * campaign's bank accounts. Matches and classifications stay as they are.
 */
export function MoveImportDialog({
  batchId,
  originalFileName,
  currentAccountId,
  concurrencyVersion,
  accounts,
  open,
  onOpenChange,
}: MoveImportDialogProps) {
  const { reattributeImport, isPending } = useReattributeImport();
  const [targetAccountId, setTargetAccountId] = useState('');
  const [refusal, setRefusal] = useState<ImportBatchRefusal | null>(null);
  const choices = accounts.filter((a) => a.id !== currentAccountId);

  const close = (next: boolean) => {
    if (!next) {
      setTargetAccountId('');
      setRefusal(null);
    }
    onOpenChange(next);
  };

  const onConfirm = async () => {
    setRefusal(null);
    try {
      await reattributeImport({ batchId, request: { newBankAccountId: targetAccountId, concurrencyVersion } });
      close(false);
    } catch (e) {
      setRefusal(importBatchRefusal(e));
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={close}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Move this import to another account?</AlertDialogTitle>
          <AlertDialogDescription>
            Every bank line from <strong>{originalFileName}</strong> moves with it. Matches and
            classifications stay as they are.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-1.5">
          <Label htmlFor="move-import-account">Move to</Label>
          <select
            id="move-import-account"
            value={targetAccountId}
            onChange={(e) => setTargetAccountId(e.target.value)}
            disabled={isPending}
            className="block min-h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="">Choose a bank account…</option>
            {choices.map((a) => (
              <option key={a.id} value={a.id}>
                {a.displayName}
              </option>
            ))}
          </select>
        </div>

        {refusal && <RefusalAlert refusal={refusal} />}

        <AlertDialogFooter>
          <Button type="button" variant="outline" onClick={() => close(false)}>
            Cancel
          </Button>
          <PendingButton
            type="button"
            pending={isPending}
            onClick={onConfirm}
            disabled={!targetAccountId || refusal?.kind === 'periodLocked'}
          >
            {isPending ? 'Moving…' : 'Move import'}
          </PendingButton>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function RefusalAlert({ refusal }: { refusal: ImportBatchRefusal }) {
  switch (refusal.kind) {
    case 'changedElsewhere':
      return <Alert>{IMPORT_CHANGED_ELSEWHERE}</Alert>;
    case 'periodLocked':
      return (
        <Alert>
          <p>{refusal.message}</p>
          {refusal.reportIds.length > 0 && (
            <ul className="mt-1.5 space-y-0.5">
              {refusal.reportIds.map((reportId) => (
                <li key={reportId}>
                  <Link
                    to="/admin/finance/reports/$reportId"
                    params={{ reportId }}
                    className="font-medium underline hover:text-amber-950"
                  >
                    Open the report to amend it
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Alert>
      );
    case 'other':
      return <Alert>{refusal.message || MOVE_FAILED}</Alert>;
    case 'undoBlocked':
      return <Alert>{MOVE_FAILED}</Alert>;
  }
}

function Alert({ children }: { children: ReactNode }) {
  return (
    <div role="alert" className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <div>{children}</div>
    </div>
  );
}
